// Fondo Cripto → Estilo & Reglas — la "constitución" del fondo común: cómo
// gestiona el equipo el riesgo, la exposición, la co-firma, las entradas y
// salidas. Las registra Plutus (definir_regla_fondo) o se editan a mano acá.
// Espacio SEPARADO del trading personal de Maxi.

import { useMemo, useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { AppLayout } from '../components/AppLayout';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { Select } from '../components/ui/Select';
import { Textarea } from '../components/ui/Textarea';
import { FondoHeader } from '../components/fund/FondoHeader';
import { RULE_CATEGORY_META, type FundRule } from '../components/fund/shared';

const CATEGORY_OPTIONS = [
  { value: 'riesgo', label: '🛡️ Riesgo' },
  { value: 'exposicion', label: '⚖️ Exposición' },
  { value: 'co-firma', label: '✍️ Co-firma' },
  { value: 'estilo', label: '🎯 Estilo' },
  { value: 'entrada', label: '📥 Entradas' },
  { value: 'salida', label: '📤 Salidas' },
  { value: 'general', label: '📌 General' },
];

const CATEGORY_ORDER = ['riesgo', 'exposicion', 'co-firma', 'estilo', 'entrada', 'salida', 'general'];

interface ListResponse {
  rules: FundRule[];
}

export default function FondoReglas(): ReactElement {
  const [showArchived, setShowArchived] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [editTarget, setEditTarget] = useState<FundRule | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['fund-rules', showArchived],
    queryFn: () =>
      api<ListResponse>(`/fund/rules${showArchived ? '?includeArchived=1' : ''}`),
  });

  const rules = data?.rules ?? [];
  const grupos = useMemo(() => {
    const by = new Map<string, FundRule[]>();
    for (const r of rules) {
      if (!by.has(r.category)) by.set(r.category, []);
      by.get(r.category)!.push(r);
    }
    return [...by.entries()].sort(
      (a, b) => CATEGORY_ORDER.indexOf(a[0]) - CATEGORY_ORDER.indexOf(b[0]),
    );
  }, [rules]);

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto px-6 py-10">
        <FondoHeader
          title="Estilo & Reglas"
          subtitle="La constitución del fondo: cómo gestionamos el riesgo, la exposición, la co-firma y los criterios de entrada y salida. Plutus la consulta y la mantiene al día."
          action={
            <Button variant="primary" onClick={() => setShowCreate(true)}>
              + Nueva regla
            </Button>
          }
        />

        <div className="flex items-center justify-end mb-6">
          <label className="flex items-center gap-2 text-xs text-ink-muted cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="accent-accent-500"
            />
            Ver archivadas
          </label>
        </div>

        {isLoading ? (
          <div className="text-ink-subtle text-sm font-mono py-12 text-center">Cargando…</div>
        ) : rules.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="space-y-10">
            {grupos.map(([cat, items]) => {
              const meta = RULE_CATEGORY_META[cat] ?? { label: cat, emoji: '📌' };
              return (
                <section key={cat}>
                  <h2 className="text-xs font-semibold text-ink-subtle uppercase tracking-[0.18em] mb-4">
                    {meta.emoji} {meta.label}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {items.map((r) => (
                      <RuleCard key={r.id} rule={r} onEdit={() => setEditTarget(r)} />
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>

      {showCreate && <RuleModal onClose={() => setShowCreate(false)} />}
      {editTarget && <RuleModal rule={editTarget} onClose={() => setEditTarget(null)} />}
    </AppLayout>
  );
}

// ── Card de una regla ────────────────────────────────────────────────────────
function RuleCard({ rule, onEdit }: { rule: FundRule; onEdit: () => void }): ReactElement {
  const qc = useQueryClient();
  const archiveMutation = useMutation({
    mutationFn: () =>
      api(`/fund/rules/${rule.id}`, { method: 'PATCH', body: { active: !rule.active } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fund-rules'] }),
  });
  const deleteMutation = useMutation({
    mutationFn: () => api(`/fund/rules/${rule.id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fund-rules'] }),
  });

  return (
    <article
      className={`bg-surface-elevated border border-surface-border rounded-xl p-5 ${
        rule.active ? '' : 'opacity-60'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-base font-bold">{rule.title}</h3>
        {!rule.active && (
          <span className="px-2 py-0.5 rounded bg-surface-panel text-ink-subtle text-[10px] uppercase tracking-wide shrink-0">
            archivada
          </span>
        )}
      </div>
      <p className="text-sm text-ink-muted mt-2 whitespace-pre-line leading-relaxed">{rule.body}</p>
      <div className="flex items-center gap-3 mt-4 pt-3 border-t border-surface-border/60 text-[11px]">
        <button onClick={onEdit} className="text-ink-subtle hover:text-accent-500 transition-colors">
          editar
        </button>
        <button
          onClick={() => archiveMutation.mutate()}
          className="text-ink-subtle hover:text-warn-DEFAULT transition-colors"
        >
          {rule.active ? 'archivar' : 'reactivar'}
        </button>
        <button
          onClick={() => deleteMutation.mutate()}
          className="text-ink-subtle hover:text-loss-DEFAULT transition-colors ml-auto"
        >
          eliminar
        </button>
      </div>
    </article>
  );
}

// ── Crear / editar regla ─────────────────────────────────────────────────────
function RuleModal({ rule, onClose }: { rule?: FundRule; onClose: () => void }): ReactElement {
  const qc = useQueryClient();
  const editing = !!rule;
  const [category, setCategory] = useState(rule?.category ?? 'general');
  const [title, setTitle] = useState(rule?.title ?? '');
  const [body, setBody] = useState(rule?.body ?? '');
  const [error, setError] = useState('');

  const saveMutation = useMutation({
    mutationFn: () =>
      editing
        ? api(`/fund/rules/${rule!.id}`, {
            method: 'PATCH',
            body: { category, title: title.trim(), body: body.trim() },
          })
        : api('/fund/rules', {
            method: 'POST',
            body: { category, title: title.trim(), body: body.trim() },
          }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fund-rules'] });
      onClose();
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Error'),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={editing ? 'Editar regla' : 'Nueva regla del fondo'}
      description="Un criterio de gestión del fondo: claro y accionable. Plutus las consulta."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            loading={saveMutation.isPending}
            disabled={title.trim().length < 3 || body.trim().length < 3}
            onClick={() => saveMutation.mutate()}
          >
            Guardar
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Select label="Categoría" value={category} onChange={setCategory} options={CATEGORY_OPTIONS} />
        <Input
          label="Título"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Riesgo máximo por trade"
        />
        <Textarea
          label="Regla / criterio"
          rows={5}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Ej: nunca arriesgar más del 2% del capital del fondo en una sola operación…"
        />
        {error && <p className="text-sm text-loss-DEFAULT">{error}</p>}
      </div>
    </Modal>
  );
}

function EmptyState(): ReactElement {
  return (
    <div className="text-center py-16 border border-dashed border-surface-border rounded-xl">
      <div className="text-4xl mb-3">📜</div>
      <h3 className="font-bold">Todavía no hay reglas escritas</h3>
      <p className="text-sm text-ink-muted mt-1 max-w-md mx-auto">
        Acá vive la constitución del fondo: riesgo, exposición, co-firma, entradas y salidas.
        Plutus las registra cuando el equipo acuerda un criterio, o las creas a mano.
      </p>
    </div>
  );
}
